import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import FormField from '../ui/FormField';
import SearchableSelect from '../SearchableSelect';

interface CourseOption {
  _id: number;
  name: string;
}

interface ICoursePickerProps {
  /** Currently selected course id (null = none). */
  value: number | null;
  onChange: (value: number | null) => void;
  /**
   * Narrow the courses to a single activity. When this changes the list is
   * refetched and the current selection is cleared if it is no longer valid.
   */
  activityId?: number | null;
  /** Optional label override (defaults to t('courses.course')). */
  label?: string;
  /** Optional help text below the select. */
  helpText?: string;
  /** Disable the select. */
  disabled?: boolean;
}

/**
 * CoursePicker — reusable single-course dropdown.
 *
 * Mirrors ActivityPicker's prop style. When an activityId is provided the list
 * is scoped to that activity and a previously-selected course that no longer
 * belongs is cleared automatically.
 */
const CoursePicker: React.FC<ICoursePickerProps> = ({
  value,
  onChange,
  activityId,
  label,
  helpText,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.courses.list(activityId != null ? { activityId } : undefined);
        if (cancelled) return;
        const mapped = (res.data as CourseOption[]).map((c) => ({
          _id: Number(c._id),
          name: c.name,
        }));
        setCourses(mapped);
        // Clear the selection if the current course isn't in the new list.
        if (value != null && !mapped.some((c) => c._id === value)) {
          onChange(null);
        }
      } catch {
        if (!cancelled) setCourses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId]);

  if (loading) {
    return <p className="form-help-text">{t('common.loading')}</p>;
  }

  return (
    <FormField htmlFor="course-picker" label={label || t('courses.course')}>
      <SearchableSelect
        id="course-picker"
        options={courses}
        value={value}
        onChange={(v) => onChange(v == null ? null : Number(v))}
        disabled={disabled}
        placeholder="—"
      />
      {helpText && <small className="form-help-text">{helpText}</small>}
    </FormField>
  );
};

export default CoursePicker;
